import { Button } from '#app/components/Buttons/Button.js';
import { ImageZoomInput } from '#app/components/ImageZoomInput/ImageZoomInput.js';
import { ModalTemplateWrapper } from '#app/components/Modals/V2/GlobalModalSystem/ModalTemplateWrapper/ModalTemplateWrapper.js';
import { logger } from '#app/services/logger.js';
import { CROPPER_ZOOM_MAX, CROPPER_ZOOM_MIN } from './constants.js';
import {
  CONTEXT_TYPE_AVATAR,
  CONTEXT_TYPE_BRAND_CUSTOMIZATION_LOGO,
  CONTEXT_TYPE_CONTACT,
  CONTEXT_TYPE_LOGO,
} from './context/actionTypes.js';
import { type ProfileImageCropperContextTypes } from './types.js';
import { useCallback, useEffect, useState } from 'react';
import {
  type Area,
  Cropper,
  type MediaSize,
  type Point,
} from 'react-easy-crop';
import { styled } from 'styled-components';

const log = logger.child({ namespace: 'ProfileImageCropperModal' });

const ModalActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  gap: 12px;
  width: 320px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const CropperContainer = styled.div`
  position: relative;
  height: 300px;
  margin-bottom: 32px;
  width: 300px;
  border-radius: 20px;
  overflow: hidden;
`;

const resolveMessages = (context: ProfileImageCropperContextTypes) => {
  const defaultMessages = {
    cancel: 'Cancel',
    headline: 'Upload image',
    save: 'Save',
  };
  const messages = {
    [CONTEXT_TYPE_AVATAR]: {
      headline: 'Crop your photo',
      subheader:
        'For best results, use a PNG, JPG, or GIF image at least 300 x 300 px.',
    },
    [CONTEXT_TYPE_BRAND_CUSTOMIZATION_LOGO]: {
      headline: 'Update brand logo',
      subheader: 'You’ll need to add a square logo. 1 MB file limit.',
    },
    [CONTEXT_TYPE_CONTACT]: {
      headline: 'Update contact photo',
      subheader:
        'For best results, use a PNG, JPG, or GIF image at least 300 x 300 px.',
    },
    [CONTEXT_TYPE_LOGO]: {
      headline: 'Update company logo',
      subheader:
        'Recommended: PNG, JPG, or GIF image at least 300 x 300 px with the logo centered. Images with white or transparent backgrounds typically work best.',
    },
  };
  const contextMessages = messages?.[context] ?? {};

  return { ...defaultMessages, ...contextMessages };
};

export const ProfileImageCropperModal = ({
  image,
  imageContext,
  isLoading,
  isOpen,
  onCancel,
  onSave,
  updateCroppedPixels,
  updateOriginalImageSize,
}: {
  readonly image: null | string;
  readonly imageContext: ProfileImageCropperContextTypes;
  readonly isLoading: boolean;
  readonly isOpen: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
  readonly updateCroppedPixels: (area: Area) => void;
  readonly updateOriginalImageSize: (size: MediaSize) => void;
}) => {
  const { cancel, headline, save, subheader } = resolveMessages(imageContext);

  const [zoom, setZoom] = useState<number>(1);

  const onZoomChange = useCallback(
    (newZoom: number) => {
      if (!isLoading) {
        setZoom(newZoom);
      }
    },
    [isLoading],
  );

  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
  });

  const onCrop = useCallback(
    (location: Point) => {
      if (!isLoading) {
        setCrop(location);
      }
    },
    [isLoading, setCrop],
  );

  const onCropComplete = useCallback(
    (_: Area, croppedAreaData: Area) => {
      if (!isLoading) {
        updateCroppedPixels(croppedAreaData);

        log.debug(croppedAreaData, 'onCropComplete');
      }
    },
    [isLoading, updateCroppedPixels],
  );

  const onLoadMedia = useCallback(
    (media: MediaSize) => {
      if (!isLoading) {
        updateOriginalImageSize(media);
      }
    },
    [isLoading, updateOriginalImageSize],
  );

  const onClose = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    onCancel();
  }, [onCancel, setCrop, setZoom]);

  useEffect(() => {
    if (!isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [isOpen]);

  return (
    <ModalTemplateWrapper
      ariaLabel="Profile Image Cropper"
      desktopModalSize="md"
      headerText={headline}
      hideCloseIcon
      isFullscreenOnMobile
      isOpen={isOpen}
      onClose={onClose}
      subheaderText={subheader}
    >
      <Body>
        <CropperContainer>
          <Cropper
            aspect={1}
            crop={crop}
            cropShape="round"
            image={image ?? undefined}
            maxZoom={CROPPER_ZOOM_MAX}
            minZoom={CROPPER_ZOOM_MIN}
            onCropChange={onCrop}
            onCropComplete={onCropComplete}
            onMediaLoaded={onLoadMedia}
            onZoomChange={onZoomChange}
            restrictPosition={false}
            showGrid={false}
            zoom={zoom}
          />
        </CropperContainer>
        <ImageZoomInput
          onZoomChange={(number_) => onZoomChange(number_ / 10)}
          value={zoom * 10}
          zoomMax={CROPPER_ZOOM_MAX * 10}
          zoomMin={CROPPER_ZOOM_MIN * 10}
        />

        <ModalActions>
          <Button
            onClick={onClose}
            variant="V2Link"
          >
            {cancel}
          </Button>
          <Button
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onSave}
            variant="V2Primary"
          >
            {save}
          </Button>
        </ModalActions>
      </Body>
    </ModalTemplateWrapper>
  );
};
